import { TDashboardTrend } from '@/types/Dashboard.types';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import {
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_STATE_LIMIT_DANGER,
  WELLNESS_STATE_LIMIT_OK,
} from '@/constants';
import useDomainScreenerInterpretation from '@/hooks/screener/useDomainScreenerInterpretation';
import { background, extra, primary, text } from '@/theme/colors/palettes';
import { THealthDomain, THealthDomainWithOverall, TSubdomainConcern } from '@/types/Health.types';
import getDomainIconFromDomain from '@/utils/getDomainIconFromDomain';
import { Center, HStack, Image, SkeletonText, Text, VStack } from '@chakra-ui/react';
import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { GOAL_NEXT_STEP_DESCRIPTION, SMART_GOALS_TEXT_LONG } from '@/constants/stringVars';
import { capitalize } from '@/utils/stringUtils';
import SubdomainInfoInterpretation from './SubdomainInfoInterpretation';
import WholenessTrends from '@/components/molecules/WholenessTrends';
import YourScore from '@/components/organisms/WholenessRingWithOverview/YourScore';
import SectionContainer from './SectionContainer';
import LoadingSection from './LoadingSection';
import SubdomainModalContent from './SubdomainModalContent';
import GrowthZoneDomainItem from '../growthZone/GrowthZoneDomainItem';
import { EGrowthDomainGoalTrend } from '@/types/GrowthZone.types';
import NavigateToGrowthZone from '@/modules/growth-zone/NavigateToGrowthZone';
import useOrganizationMemberCares from '@/hooks/membercare/useOrganizationMemberCares';
import { useNavigate } from '@tanstack/react-router';
import Calendly from '@/components/molecules/Calendly';
import svgIcons from '@/assets/svg/icons';
import MemberCareCircledIcon from '@/components/atoms/MemberCareCircledIcon';

interface Props {
  selectedDomain: THealthDomainWithOverall | undefined;
  setSelectedDomain: Dispatch<SetStateAction<THealthDomainWithOverall | undefined>>;
  selectedDomainScore: number;
}

const DomainInterpretationModal = ({ selectedDomain, setSelectedDomain, selectedDomainScore }: Props) => {
  const navigate = useNavigate();

  const IconComponent = selectedDomain ? getDomainIconFromDomain(selectedDomain) : undefined;
  const domainColor =
    selectedDomainScore < WELLNESS_STATE_LIMIT_DANGER
      ? extra.red
      : selectedDomainScore > WELLNESS_STATE_LIMIT_OK
        ? extra.teal
        : extra.yellow;

  const {
    data: domainInterpretation,
    isLoading,
    refetch,
    isRefetching,
  } = useDomainScreenerInterpretation(selectedDomain ?? 'overall');

  const selectedDomainDashboardTrendData: Partial<TDashboardTrend> | null =
    selectedDomain && selectedDomain !== 'overall'
      ? {
          [selectedDomain]: domainInterpretation?.trends.map((trend) => ({
            month: trend.month,
            year: trend.year,
            score: trend[selectedDomain],
          })) ?? [],
        }
      : null;

  const [selectedSubdomainConcern, setSelectedSubdomainConcern] = useState<TSubdomainConcern | undefined>(undefined);

  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: memberCares } = useOrganizationMemberCares();
  const hasJustOneMemberCare = memberCares?.length === 1;
  const firstMemberCare = memberCares?.[0];

  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  return (
    <ModalContainer
      isBlur={false}
      isOpen={!!selectedDomain}
      onClose={() => {
        setSelectedDomain(undefined);
        setSelectedSubdomainConcern(undefined);
      }}
      width={'50vw'}
      height={'80vh'}
      scrollOverflow={false}
      backgroundColor={background.lightBlue}
    >
      {selectedDomain ? (
        <VStack style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <VStack
            width={'100%'}
            height={'80vh'}
            alignItems={'center'}
            paddingTop={'35px'}
            overflowY={'scroll'}
            paddingBottom={'14px'}
            marginRight={'-25px'}
            paddingRight={'20px'}
            boxSizing="content-box"
            ref={scrollRef}
          >
            {selectedSubdomainConcern ? (
              <SubdomainModalContent
                domain={selectedDomain}
                subdomainConcern={selectedSubdomainConcern}
                resetSelectedSubdomainConcern={() => {
                  scrollRef.current?.scrollTo({ top: 0 });
                  setSelectedSubdomainConcern(undefined);
                }}
              />
            ) : (
              <VStack width={'80%'} gap={'24px'}>
                <HStack gap={'20px'}>
                  <Center
                    backgroundColor={extra.white}
                    borderRadius={'50%'}
                    padding={'12px'}
                    boxShadow={'0px 6.4px 32px 0px #0041791A'}
                  >
                    {IconComponent && <IconComponent color={domainColor} size={36} background="transparent" />}
                  </Center>

                  <Text variant={'loraSectionTitle'}>
                    {WELLNESS_DOMAINS_DISPLAY_LABELS[selectedDomain as THealthDomain]} Wholeness{' '}
                    {selectedDomain === 'overall' && 'Overview'}
                  </Text>
                </HStack>

                <SectionContainer>
                  <YourScore score={selectedDomainScore} horizontal svgId={selectedDomain} />
                </SectionContainer>

                {/* What this means: */}
                <SectionContainer>
                  {selectedDomain !== 'overall' && (
                    <Text variant={'loraExtraSmallTitle'} color={text.mediumGray}>
                      What this means:
                    </Text>
                  )}
                  {isLoading ? (
                    <SkeletonText
                      height={'40px'}
                      width={'100%'}
                      startColor={text.mediumBlue}
                      endColor={text.mediumGray}
                      fadeDuration={2}
                    />
                  ) : (
                    <Text variant={'urbanistMedium18'} color={text.mediumBlue} textAlign={'start'}>
                      {domainInterpretation?.interpretation.description}
                    </Text>
                  )}
                </SectionContainer>

                {isLoading && (
                  <>
                    <LoadingSection />
                    <LoadingSection />
                    <LoadingSection />
                    <LoadingSection />
                  </>
                )}

                {/* Areas Of Focus: */}
                {domainInterpretation?.concerns && domainInterpretation.concerns.length > 0 && (
                  <SectionContainer>
                    <VStack width={'100%'} alignItems={'start'}>
                      <Text
                        variant={'loraExtraSmallTitle'}
                        color={text.mediumBlue}
                        marginBottom={1}
                        textAlign={'start'}
                      >
                        Areas of Focus:
                      </Text>
                      <VStack width={'100%'} gap={4} alignItems={'start'}>
                        {domainInterpretation?.concerns.map((subdomainConcern) => (
                          <SubdomainInfoInterpretation
                            key={subdomainConcern.subdomain}
                            subdomainConcern={subdomainConcern}
                            onClickSubdomain={() => {
                              setSelectedSubdomainConcern(subdomainConcern);
                              scrollRef.current?.scrollTo({ top: 0 });
                            }}
                          />
                        ))}
                      </VStack>
                    </VStack>
                  </SectionContainer>
                )}

                {/* What's Your Next Step? */}
                <SectionContainer>
                  <VStack gap={2} width={'100%'}>
                    {domainInterpretation?.goals && domainInterpretation.goals.length > 0 ? (
                      <HStack width={'100%'} justify="space-between" alignItems={'center'}>
                        <Text variant={'loraExtraSmallTitle'} color={primary[500]}>
                          Your {capitalize(selectedDomain ?? '')} Goals
                        </Text>
                        <NavigateToGrowthZone />
                      </HStack>
                    ) : (
                      <VStack alignItems={'start'}>
                        <HStack width={'100%'} justify="space-between" alignItems={'center'}>
                          <Text variant={'loraExtraSmallTitle'} color={primary[500]}>
                            What's Your Next Step?
                          </Text>
                          <NavigateToGrowthZone />
                        </HStack>
                        <Text
                          variant={'urbanistMediumLarge'}
                          color={text.mediumBlue}
                          paddingBottom={'4px'}
                          paddingTop={'2px'}
                        >
                          {GOAL_NEXT_STEP_DESCRIPTION}
                        </Text>
                        <Text variant={'urbanistBold'} color={'text.mediumGray'}>
                          {SMART_GOALS_TEXT_LONG}
                        </Text>
                      </VStack>
                    )}

                    <VStack width={'100%'} gap={1}>
                      <GrowthZoneDomainItem
                        domainGoalData={{
                          createdAt: '',
                          domain: selectedDomain,
                          goals: domainInterpretation?.goals ?? [],
                          score: 1,
                          trend: EGrowthDomainGoalTrend.IMPROVING,
                        }}
                        headerHidden
                        showMultipleCreateItems
                        refetchAllGoals={refetch}
                        isRefetchingAllGoals={isRefetching}
                      />
                    </VStack>
                  </VStack>
                </SectionContainer>

                {/* Wholeness Trend */}
                {selectedDomain !== 'overall' && (
                  <HStack width={'100%'} paddingX={2}>
                    <WholenessTrends
                      chartHeight={265}
                      dashboardTrendData={selectedDomainDashboardTrendData}
                      // in case we need to add chart for Overall too
                      // preMappedData={selectedDomain === 'overall' ? selectedDomainDashboardTrendData : undefined}
                      // preSelectedDomain={selectedDomain === 'overall' ? undefined : selectedDomain}
                      preSelectedDomain={selectedDomain}
                    />
                  </HStack>
                )}

                <VStack width={'100%'} alignItems={'center'} marginY={'20px'}>
                  <Text variant={'loraExtraSmallTitle'} color={text.mediumBlue}>
                    Want to Talk to Your Member Care Coordinator?
                  </Text>
                  <HStack width={'100%'} justify={'space-between'} alignItems={'center'} gap={'12px'} marginY={'10px'}>
                    <HStack alignItems={'center'}>
                      {hasJustOneMemberCare ? (
                        <Image
                          src={firstMemberCare.profilePicture}
                          alt="member-care-avatar"
                          resize={'block'}
                          objectFit={'contain'}
                          style={{ width: 60, height: 60, margin: '10px', borderRadius: '50%' }}
                          boxShadow="0px 6px 24px 0px #0E006233"
                        />
                      ) : (
                        <MemberCareCircledIcon />
                      )}
                      <VStack alignItems={'start'} gap={'4px'}>
                        {hasJustOneMemberCare ? (
                          <>
                            <Text variant={'loraMedium18'} color={text.darkBlue}>
                              {firstMemberCare.firstName} {firstMemberCare.lastName}
                            </Text>
                            <Text variant={'urbanistSemiBold'} fontSize={14} fontWeight={'bold'} color={text.darkBlue}>
                              Member Care Specialist
                            </Text>
                          </>
                        ) : (
                          <>
                            <Text variant={'urbanistBold'} color={'text.darkBlue'}>
                              {memberCares ? `Your organization has ${memberCares.length}` : "Your organization's"}
                            </Text>
                            <Text variant={'urbanistBold'} color={'text.darkBlue'}>
                              Member Care Specialists
                            </Text>
                          </>
                        )}
                      </VStack>
                    </HStack>

                    <CustomButton
                      labelColor="white"
                      backgroundColor="secondary.500"
                      label={hasJustOneMemberCare ? `Contact Member Care` : `Member Care Team`}
                      iconAfterLabelSvgPath={svgIcons.rightArrow}
                      iconAfterLabelSvgSize={14}
                      onClick={() =>
                        hasJustOneMemberCare ? setIsCalendlyOpen(true) : navigate({ to: '/directory/member-care' })
                      }
                      height={'54px'}
                      width={'220px'}
                    />
                    {hasJustOneMemberCare && firstMemberCare && (
                      <Calendly
                        isOpen={isCalendlyOpen}
                        setIsOpen={setIsCalendlyOpen}
                        url={firstMemberCare.calendly_link}
                      />
                    )}
                  </HStack>
                </VStack>
              </VStack>
            )}
          </VStack>
        </VStack>
      ) : null}
    </ModalContainer>
  );
};

export default DomainInterpretationModal;
